/**
 * URL utilities for code sharing functionality
 */

import LZString from "lz-string";

/**
 * Compresses and encodes text using LZ-string compression for URL usage
 */
export const compressAndEncode = (text: string): string => {
	try {
		return LZString.compressToEncodedURIComponent(text);
	} catch (error) {
		console.warn("Failed to compress and encode text:", error);
		return "";
	}
};

/**
 * Decompresses and decodes text from LZ-string compression
 */
export const decompressAndDecode = (compressed: string): string => {
	try {
		const decompressed = LZString.decompressFromEncodedURIComponent(compressed);
		return decompressed || "";
	} catch (error) {
		console.warn("Failed to decompress and decode text:", error);
		return "";
	}
};

// Legacy base64 functions for backward compatibility
/**
 * Encodes a string to base64 with proper UTF-8 handling
 * @deprecated Use compressAndEncode instead for better compression
 */
export const encodeToBase64 = (text: string): string => {
	try {
		return btoa(unescape(encodeURIComponent(text)));
	} catch (error) {
		console.warn("Failed to encode text to base64:", error);
		return "";
	}
};

/**
 * Decodes a base64 string with proper UTF-8 handling
 * @deprecated Use decompressAndDecode instead
 */
export const decodeFromBase64 = (encoded: string): string => {
	try {
		return decodeURIComponent(escape(atob(encoded)));
	} catch (error) {
		console.warn("Failed to decode base64 text:", error);
		return "";
	}
};

/**
 * Result of parsing shared code from URL
 */
export interface ParseSharedCodeResult {
	success: boolean;
	code?: string;
	error?: string;
}

/**
 * Parses URL query parameters and extracts shared code
 */
export const parseSharedCodeFromURL = (): ParseSharedCodeResult => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		const encodedCode = urlParams.get("code");

		// Check hash for backward compatibility
		let hashEncodedCode = "";
		const hash = window.location.hash.substring(1);
		if (hash && !encodedCode) {
			const hashParams = new URLSearchParams(hash);
			hashEncodedCode = hashParams.get("code") || "";
		}

		const codeToProcess = encodedCode || hashEncodedCode;

		// If there's no code parameter in either location
		if (!codeToProcess) {
			return { success: false };
		}

		// Try new compression format first
		let decodedCode = decompressAndDecode(codeToProcess);

		// Fall back to legacy base64 decoding if compression fails
		if (!decodedCode) {
			decodedCode = decodeFromBase64(codeToProcess);
		}

		if (!decodedCode) {
			return { success: false, error: "Failed to decode shared code from URL" };
		}

		return { success: true, code: decodedCode };
	} catch (error) {
		console.warn("Failed to parse URL parameters:", error);
		return {
			success: false,
			error:
				error instanceof Error
					? error.message
					: "Failed to parse shared code from URL",
		};
	}
};

/**
 * Generates a shareable URL for the given code using compression
 */
export const generateShareableURL = (code: string): string => {
	const compressedCode = compressAndEncode(code);

	if (!compressedCode || !code.trim()) {
		return window.location.origin;
	}

	const params = new URLSearchParams();
	params.set("code", compressedCode);

	return `${window.location.origin}?${params.toString()}`;
};

/**
 * Clears the URL query parameters (useful after loading shared code)
 */
export const clearURLParams = (): void => {
	const url = new URL(window.location.href);
	url.search = "";
	url.hash = ""; // Also clear hash for backward compatibility
	window.history.replaceState(null, "", url.toString());
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use clearURLParams instead
 */
export const clearURLHash = (): void => {
	clearURLParams();
};

/**
 * Checks if the current URL contains shared code
 */
export const hasSharedCodeInURL = (): boolean => {
	const urlParams = new URLSearchParams(window.location.search);
	const hasQueryCode = urlParams.has("code");

	// Check hash for backward compatibility
	const hash = window.location.hash.substring(1);
	const hashParams = new URLSearchParams(hash);
	const hasHashCode = hashParams.has("code");

	return hasQueryCode || hasHashCode;
};
